/**
 * App Globals Store — React-native source of truth for boot-time global state.
 *
 * Replaces the AngularJS `$rootScope.G_*` reads scattered across ~50 files. Each
 * value is fetched once at app startup (by `StandaloneBootstrap` in standalone
 * mode, or read from `$rootScope` in embedded mode) and exposed via selector
 * hooks so consumers re-render reactively when a slice changes.
 *
 * Design choices:
 *   - **One store, many slices.** These globals share a lifecycle ("populated
 *     once on boot, occasionally updated") so a single store keeps the API
 *     small. Per-slice selectors give the same render-isolation as separate
 *     stores would.
 *   - **Dual-write to `$rootScope` in embedded mode.** Until every AngularJS
 *     consumer is gone, mutations also flow back via `setRootScopeProperty`
 *     so the legacy shell stays in sync.
 *   - **Pure no-op in standalone mode** for the legacy bridge — the store is
 *     the only source of truth.
 */

import { create } from 'zustand';
import { setRootScopeProperty, getRootScopeProperty } from '@/lib/legacyContext';
import { getMountConfig } from '@/mountConfig';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GSettings {
  enableBackButtons?: boolean;
  enableRecentOrders?: boolean;
  theme?: string;
  primary?: string;
  secondary?: string;
  loadingSpinner?: string;
  showAllResults?: boolean;
  rebrand?: boolean;
  showBidIds?: boolean;
  showRecordKeys?: boolean;
  [key: string]: unknown;
}

export interface AllowedTransition {
  fromState?: string;
  toState?: string;
  permission?: string;
  [key: string]: unknown;
}

export interface FinancialWorkflow {
  displayStatus?: string;
  currentState?: { code?: string; name?: string };
  [key: string]: unknown;
}

export interface VendorWorkflow {
  code?: string;
  name?: string;
  [key: string]: unknown;
}

interface AppGlobalsState {
  /** Current user (RoleMaster + /API/Partial/GetCurrentUsername) */
  currentUser: string | null;
  /** SafeView image-viewer URL prefix */
  imagePrefix: string;
  /** Browser tab title — also written to document.title in embedded mode */
  pageTitle: string;
  /** Per-user UI settings persisted to localStorage */
  settings: GSettings;
  /** Whether ST/automation-data UX is enabled for the current tenant */
  applySTLogic: boolean;
  /** Permission keys that should ONLY apply during Phase V */
  phaseVOnlyPermissions: string[];
  /** Current work-order phase (PHASEI / PHASEV / etc.) */
  workOrderPhase: string | null;
  /** Workflow transitions allowed by the current role */
  allowedWorkOrderTransitions: AllowedTransition[];
  /** Source systems whose orders auto-accept */
  acceptAllSourceSystems: string[];
  /** Question types whose max-date is "current" */
  maxDateQuestions: string[];
  /** Workflow code → financial workflow detail */
  financialWorkflow: FinancialWorkflow | null;
  /** Workflow code → vendor workflow detail */
  vendorWorkflow: VendorWorkflow | null;
  /** Help URL for the floating help button */
  helpUrl: string;
  /** Feedback button mailto target */
  feedbackEmail: string;
  /** Build info string for the footer */
  buildInfo: string | null;
  /** Workflow state codes used for task scheduling */
  tasksWFSCodes: string[];
  /** US states reference data */
  states: unknown[];
}

interface AppGlobalsActions {
  /** Replace one slice and dual-write to `$rootScope` in embedded mode. */
  setCurrentUser: (v: string | null) => void;
  setImagePrefix: (v: string) => void;
  setPageTitle: (v: string) => void;
  setSettings: (v: GSettings) => void;
  setApplySTLogic: (v: boolean) => void;
  setPhaseVOnlyPermissions: (v: string[]) => void;
  setWorkOrderPhase: (v: string | null) => void;
  setAllowedWorkOrderTransitions: (v: AllowedTransition[]) => void;
  setAcceptAllSourceSystems: (v: string[]) => void;
  setMaxDateQuestions: (v: string[]) => void;
  setFinancialWorkflow: (v: FinancialWorkflow | null) => void;
  setVendorWorkflow: (v: VendorWorkflow | null) => void;
  setHelpUrl: (v: string) => void;
  setFeedbackEmail: (v: string) => void;
  setBuildInfo: (v: string | null) => void;
  setTasksWFSCodes: (v: string[]) => void;
  setStates: (v: unknown[]) => void;

  /** Bulk-set many slices (used by `StandaloneBootstrap`). No legacy sync. */
  hydrate: (patch: Partial<AppGlobalsState>) => void;
  /** Read every slice from `$rootScope` (embedded mode startup helper). */
  hydrateFromLegacy: () => void;
  /** Reset everything to defaults — used in tests. */
  reset: () => void;
}

type AppGlobalsStore = AppGlobalsState & AppGlobalsActions;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INITIAL_STATE: AppGlobalsState = {
  currentUser: null,
  imagePrefix: '',
  pageTitle: '',
  settings: {},
  applySTLogic: false,
  phaseVOnlyPermissions: [],
  workOrderPhase: null,
  allowedWorkOrderTransitions: [],
  acceptAllSourceSystems: [],
  maxDateQuestions: [],
  financialWorkflow: null,
  vendorWorkflow: null,
  helpUrl: '',
  feedbackEmail: '',
  buildInfo: null,
  tasksWFSCodes: [],
  states: [],
};

/**
 * Mirror a slice change to AngularJS `$rootScope` so the legacy shell sees the
 * same value. No-op in standalone mode (where `setRootScopeProperty` writes to
 * an in-memory fallback that no one reads).
 */
function syncToLegacy(key: string, value: unknown): void {
  try {
    if (getMountConfig().mode === 'embedded') {
      setRootScopeProperty(key, value);
    }
  } catch {
    // Graceful degradation if AngularJS is unavailable.
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAppGlobalsStore = create<AppGlobalsStore>()((set) => ({
  ...INITIAL_STATE,

  setCurrentUser: (v) => {
    set({ currentUser: v });
    syncToLegacy('currentuser', v);
  },
  setImagePrefix: (v) => {
    set({ imagePrefix: v });
    syncToLegacy('imageprefix', v);
  },
  setPageTitle: (v) => {
    set({ pageTitle: v });
    syncToLegacy('G_PAGE_TITLE', v);
    try {
      if (typeof document !== 'undefined' && v) document.title = v;
    } catch {
      // jsdom or restricted env
    }
  },
  setSettings: (v) => {
    set({ settings: v });
    syncToLegacy('G_SETTINGS', v);
  },
  setApplySTLogic: (v) => {
    set({ applySTLogic: v });
    syncToLegacy('ApplySTLogic', v);
  },
  setPhaseVOnlyPermissions: (v) => {
    set({ phaseVOnlyPermissions: v });
    syncToLegacy('phaseVOnlyPermissions', v);
  },
  setWorkOrderPhase: (v) => {
    set({ workOrderPhase: v });
    syncToLegacy('WORK_ORDER_PHASE', v);
  },
  setAllowedWorkOrderTransitions: (v) => {
    set({ allowedWorkOrderTransitions: v });
    syncToLegacy('allowedWorkOrderTransitions', v);
  },
  setAcceptAllSourceSystems: (v) => {
    set({ acceptAllSourceSystems: v });
    syncToLegacy('getAcceptAllSourceSystems', v);
  },
  setMaxDateQuestions: (v) => {
    set({ maxDateQuestions: v });
    syncToLegacy('maxDateQuestions', v);
  },
  setFinancialWorkflow: (v) => {
    set({ financialWorkflow: v });
    syncToLegacy('G_WORKORDER_FINANCIAL_WORKFLOW', v);
  },
  setVendorWorkflow: (v) => {
    set({ vendorWorkflow: v });
    syncToLegacy('G_WORKORDER_VENDOR_WORKFLOW', v);
  },
  setHelpUrl: (v) => {
    set({ helpUrl: v });
    // legacy code stores this as a window global, not $rootScope — skip sync
  },
  setFeedbackEmail: (v) => {
    set({ feedbackEmail: v });
  },
  setBuildInfo: (v) => {
    set({ buildInfo: v });
  },
  setTasksWFSCodes: (v) => {
    set({ tasksWFSCodes: v });
  },
  setStates: (v) => {
    set({ states: v });
  },

  hydrate: (patch) => {
    set(patch);
  },

  hydrateFromLegacy: () => {
    // Embedded-only — see propertyStore.hydrateFromLegacy for full reasoning.
    if (getMountConfig().mode !== 'embedded') return;

    const settings = getRootScopeProperty<GSettings>('G_SETTINGS') ?? {};
    const allowed = getRootScopeProperty<AllowedTransition[]>('allowedWorkOrderTransitions') ?? [];
    const phaseV = getRootScopeProperty<string[]>('phaseVOnlyPermissions') ?? [];
    const sources = getRootScopeProperty<string[]>('getAcceptAllSourceSystems') ?? [];
    const maxDates = getRootScopeProperty<string[]>('maxDateQuestions') ?? [];
    set({
      currentUser: getRootScopeProperty<string>('currentuser') ?? null,
      imagePrefix: getRootScopeProperty<string>('imageprefix') ?? '',
      pageTitle: getRootScopeProperty<string>('G_PAGE_TITLE') ?? '',
      settings,
      applySTLogic: Boolean(getRootScopeProperty<boolean>('ApplySTLogic')),
      phaseVOnlyPermissions: phaseV,
      workOrderPhase: getRootScopeProperty<string>('WORK_ORDER_PHASE') ?? null,
      allowedWorkOrderTransitions: allowed,
      acceptAllSourceSystems: sources,
      maxDateQuestions: maxDates,
      financialWorkflow:
        getRootScopeProperty<FinancialWorkflow>('G_WORKORDER_FINANCIAL_WORKFLOW') ?? null,
      vendorWorkflow: getRootScopeProperty<VendorWorkflow>('G_WORKORDER_VENDOR_WORKFLOW') ?? null,
    });
  },

  reset: () => {
    set({ ...INITIAL_STATE });
  },
}));

// ---------------------------------------------------------------------------
// Convenience selectors — consume one slice at a time for narrow re-renders
// ---------------------------------------------------------------------------

export const useCurrentUser = (): string | null => useAppGlobalsStore((s) => s.currentUser);
export const useImagePrefix = (): string => useAppGlobalsStore((s) => s.imagePrefix);
export const usePageTitleSlice = (): string => useAppGlobalsStore((s) => s.pageTitle);
export const useSettings = (): GSettings => useAppGlobalsStore((s) => s.settings);
export const useApplySTLogic = (): boolean => useAppGlobalsStore((s) => s.applySTLogic);
export const usePhaseVOnlyPermissions = (): string[] =>
  useAppGlobalsStore((s) => s.phaseVOnlyPermissions);
export const useWorkOrderPhase = (): string | null => useAppGlobalsStore((s) => s.workOrderPhase);
export const useAllowedWorkOrderTransitions = (): AllowedTransition[] =>
  useAppGlobalsStore((s) => s.allowedWorkOrderTransitions);
export const useAcceptAllSourceSystems = (): string[] =>
  useAppGlobalsStore((s) => s.acceptAllSourceSystems);
export const useMaxDateQuestions = (): string[] => useAppGlobalsStore((s) => s.maxDateQuestions);
export const useFinancialWorkflow = (): FinancialWorkflow | null =>
  useAppGlobalsStore((s) => s.financialWorkflow);
export const useVendorWorkflow = (): VendorWorkflow | null =>
  useAppGlobalsStore((s) => s.vendorWorkflow);
export const useTasksWFSCodes = (): string[] => useAppGlobalsStore((s) => s.tasksWFSCodes);
export const useStates = (): unknown[] => useAppGlobalsStore((s) => s.states);

/** Test-only — reset module state. */
export function __resetAppGlobalsForTests(): void {
  useAppGlobalsStore.setState({ ...INITIAL_STATE });
}
